import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronUp, Loader2, X } from 'lucide-react';
import {
    Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { getRuleSeries } from '@/services/api/price-structure';
import type { RuleSeriesPoint } from '@/types/price-structure';

/**
 * "Data analysis" da regra, no formato da Active View: o desempenho no GAM dia
 * a dia com o valor da regra por cima.
 *
 * O relatório de key-values do GAM é diário — um ponto por dia, não por hora.
 * Valores em US$ no eixo da esquerda; PMR e CTR, em %, no da direita.
 */

type SeriesKey = 'rule' | 'opening' | 'revenue' | 'ecpm' | 'pmr' | 'ctr';

interface SeriesDef {
    key: SeriesKey;
    label: string;
    color: string;
    axis: 'usd' | 'pct';
    kind: 'line' | 'area';
    hint: string;
}

const SERIES: SeriesDef[] = [
    { key: 'rule', label: 'Regra', color: '#111827', axis: 'usd', kind: 'line',
        hint: 'Regra do otimizador. Em sombra: calculada todo dia, não entra na cascata.' },
    { key: 'opening', label: 'Abre em', color: '#0284c7', axis: 'usd', kind: 'line',
        hint: '1º degrau da cascata que estava no ar naquele dia.' },
    { key: 'revenue', label: 'Receita', color: '#fb923c', axis: 'usd', kind: 'area', hint: 'Receita do dia no GAM' },
    { key: 'ecpm', label: 'eCPM', color: '#60a5fa', axis: 'usd', kind: 'area', hint: 'Receita ÷ impressões × 1000' },
    { key: 'pmr', label: 'PMR', color: '#f472b6', axis: 'pct', kind: 'area', hint: 'Respostas servidas ÷ ad requests elegíveis' },
    { key: 'ctr', label: 'CTR', color: '#8b5cf6', axis: 'pct', kind: 'area', hint: 'Cliques ÷ impressões' },
];

const DAY_OPTIONS = [7, 14, 30];

const money = (v: number) =>
    `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortMoney = (v: number) =>
    v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtDay = (iso: string, opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' }) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', opts);

export function RuleChart({ setupId, adUnit, country, customValue, title, onClose }: {
    setupId: number;
    adUnit: string;
    country: string;
    customValue: string;
    title: string;
    onClose: () => void;
}) {
    const [days, setDays] = useState(7);
    const [collapsed, setCollapsed] = useState(false);
    const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
        rule: true, opening: true, revenue: true, ecpm: true, pmr: true, ctr: true,
    });

    const query = useQuery({
        queryKey: ['price-structure-rule-series', setupId, adUnit, country, customValue, days],
        queryFn: () =>
            getRuleSeries(setupId, { ad_unit: adUnit, country, custom_value: customValue, days }),
        staleTime: 5 * 60 * 1000,
    });

    const points = query.data?.points ?? [];
    const active = SERIES.filter((s) => visible[s.key]);
    const hasPct = active.some((s) => s.axis === 'pct');
    const hasUsd = active.some((s) => s.axis === 'usd');

    return (
        <div className="rounded-xl border border-gray-200 bg-white">
            <div className="flex items-start justify-between gap-4 px-5 pt-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-3">
                        <h4 className="truncate text-sm font-semibold text-gray-900">
                            Análise · {title}
                        </h4>
                        <button
                            type="button"
                            onClick={() => setCollapsed((v) => !v)}
                            className="text-gray-500 hover:text-gray-900"
                            title={collapsed ? 'Mostrar gráfico' : 'Recolher gráfico'}
                        >
                            <ChevronUp className={`h-4 w-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
                        </button>
                    </div>
                    {query.data && (
                        <div className="mt-0.5 text-xs text-gray-500">
                            {fmtDay(query.data.start, { day: '2-digit', month: 'short', year: 'numeric' })}
                            {' – '}
                            {fmtDay(query.data.end, { day: '2-digit', month: 'short', year: 'numeric' })}
                            {' · '}
                            {country} · {adUnit}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex overflow-hidden rounded-lg border border-gray-200 text-xs">
                        {DAY_OPTIONS.map((d) => (
                            <button
                                key={d}
                                type="button"
                                onClick={() => setDays(d)}
                                className={`px-2.5 py-1 ${
                                    d === days ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {d}d
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-gray-200 p-1 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        title="Fechar"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {!collapsed && (
                <>
                    <div className="flex flex-wrap gap-x-5 gap-y-2 border-b border-gray-100 px-5 py-3">
                        {SERIES.map((s) => (
                            <label
                                key={s.key}
                                className="inline-flex cursor-pointer select-none items-center gap-2 text-sm text-gray-700"
                                title={s.hint}
                            >
                                <input
                                    type="checkbox"
                                    checked={visible[s.key]}
                                    onChange={() => setVisible((v) => ({ ...v, [s.key]: !v[s.key] }))}
                                    className="h-4 w-4 rounded border-gray-300 accent-emerald-500"
                                />
                                {s.label}
                            </label>
                        ))}
                    </div>

                    <div className="h-[280px] px-2 py-3">
                        {query.isLoading ? (
                            <div className="flex h-full items-center justify-center gap-2 text-sm text-gray-500">
                                <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> Consultando o GAM...
                            </div>
                        ) : query.isError ? (
                            <div className="flex h-full items-center justify-center text-sm text-rose-600">
                                Não foi possível carregar a série do GAM.
                            </div>
                        ) : !points.some((p) => p.impressions > 0 || p.rule != null) ? (
                            <div className="flex h-full items-center justify-center text-sm text-gray-500">
                                Sem dados do GAM para esta combinação no período.
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                                    <defs>
                                        {SERIES.filter((s) => s.kind === 'area').map((s) => (
                                            <linearGradient key={s.key} id={`rule-grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={s.color} stopOpacity={0.55} />
                                                <stop offset="100%" stopColor={s.color} stopOpacity={0.08} />
                                            </linearGradient>
                                        ))}
                                    </defs>
                                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={(d: string) => fmtDay(d)}
                                        tick={{ fontSize: 11, fill: '#6b7280' }}
                                        axisLine={{ stroke: '#e5e7eb' }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        yAxisId="usd"
                                        hide={!hasUsd}
                                        tickFormatter={shortMoney}
                                        tick={{ fontSize: 11, fill: '#6b7280' }}
                                        axisLine={false}
                                        tickLine={false}
                                        width={56}
                                    />
                                    <YAxis
                                        yAxisId="pct"
                                        orientation="right"
                                        hide={!hasPct}
                                        tickFormatter={(v: number) => `${v}%`}
                                        tick={{ fontSize: 11, fill: '#6b7280' }}
                                        axisLine={false}
                                        tickLine={false}
                                        width={44}
                                    />
                                    <Tooltip content={<ChartTooltip />} />
                                    {/* Áreas primeiro, linhas por cima — a regra nunca fica escondida. */}
                                    {active.filter((s) => s.kind === 'area').map((s) => (
                                        <Area
                                            key={s.key}
                                            yAxisId={s.axis}
                                            dataKey={s.key}
                                            name={s.label}
                                            type="monotone"
                                            stroke={s.color}
                                            strokeWidth={1.5}
                                            fill={`url(#rule-grad-${s.key})`}
                                            isAnimationActive={false}
                                        />
                                    ))}
                                    {active.filter((s) => s.kind === 'line').map((s) => (
                                        <Line
                                            key={s.key}
                                            yAxisId={s.axis}
                                            dataKey={s.key}
                                            name={s.label}
                                            type="monotone"
                                            stroke={s.color}
                                            strokeWidth={1.5}
                                            strokeDasharray={s.key === 'rule' ? '6 4' : undefined}
                                            dot={{ r: 3, fill: s.color, strokeWidth: 0 }}
                                            connectNulls
                                            isAnimationActive={false}
                                        />
                                    ))}
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 border-t border-gray-100 px-5 py-3 text-sm text-gray-700">
                        {active.map((s) => (
                            <span key={s.key} className="inline-flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                                {s.label}
                            </span>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function ChartTooltip({ active, payload, label }: {
    active?: boolean;
    payload?: { payload: RuleSeriesPoint }[];
    label?: string;
}) {
    if (!active || !payload?.length || !label) return null;
    const p = payload[0].payload;
    const row = (name: string, value: string, color: string) => (
        <div className="flex items-center justify-between gap-6">
            <span className="inline-flex items-center gap-1.5 text-gray-600">
                <span className="h-2 w-2 rounded-full" style={{ background: color }} />
                {name}
            </span>
            <span className="font-medium tabular-nums text-gray-900">{value}</span>
        </div>
    );
    return (
        <div className="min-w-[200px] space-y-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
            <div className="mb-1 font-semibold text-gray-900">
                {fmtDay(label, { weekday: 'short', day: '2-digit', month: 'short' })}
            </div>
            {row('Regra', p.rule == null ? '—' : money(p.rule), '#111827')}
            {row('Abre em', p.opening == null ? '—' : money(p.opening), '#0284c7')}
            {row('Receita', money(p.revenue), '#fb923c')}
            {row('eCPM', money(p.ecpm), '#60a5fa')}
            {row('PMR', `${p.pmr.toFixed(1)}%`, '#f472b6')}
            {row('CTR', `${p.ctr.toFixed(1)}%`, '#8b5cf6')}
            {row('Impressões', p.impressions.toLocaleString('pt-BR'), '#d1d5db')}
        </div>
    );
}
